import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import { ShowHeader } from '@/common/components/ShowHeader'
import { useGoBack } from '@/common/hooks/useGoBack'
import { FormTransformers } from '@/common/utils/formTransformers'
import TmaisFaqForm from '@/features/tmaisFaqs/components/TmaisFaqForm'
import { useTmaisFaq } from '@/features/tmaisFaqs/hooks/useTmaisFaq'
import useUpdateTmaisFaq from '@/features/tmaisFaqs/hooks/useUpdateTmaisFaq'
import { tmaisFaqFormSchema, TmaisFaqFormSchema } from '@/features/tmaisFaqs/schemas/form.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

const UpdateTmaisFaq = () => {
  const { id = '' } = useParams()
  const goBack = useGoBack()
  const queryClient = useQueryClient()

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { data: faq, isLoading: faqIsLoading, error: faqError = {} as any, refetch: refetchFaq } = useTmaisFaq(id)

  const form = useForm<TmaisFaqFormSchema>({
    resolver: zodResolver(tmaisFaqFormSchema),
    defaultValues: {
      question: '',
      answer: '',
      position: '0',
    },
  })

  const { handleSubmit, setError, reset } = form

  useEffect(() => {
    if (!faqIsLoading) {
      reset({
        question: faq?.question,
        answer: faq?.answer,
        position: faq?.position?.toString() ?? '0',
      })
    }
  }, [faqIsLoading])

  const { mutate: updateFaq, isPending: updateFaqIsPending } = useUpdateTmaisFaq({
    id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tmaisFaqs'] })
      queryClient.invalidateQueries({ queryKey: ['tmaisFaq', id] })
      goBack({ fallback: `/tmais-faqs/${id}` })
    },
    onError: FormTransformers.errorsTransformer({ setError, setMessages }),
  })

  const onSubmit: SubmitHandler<TmaisFaqFormSchema> = (values) => updateFaq(values)

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="off" className="inner-screen">
      <ShowHeader
        goBack
        goBackProps={{ fallback: `/tmais-faqs` }}
        title="FAQs"
        subtitle={faq?.question}
        isLoading={faqIsLoading}
        buttons={
          <div className="flex items-center gap-3">
            <Button type="button" variant="outline-error" onClick={() => goBack({ fallback: `/tmais-faqs/${id}` })}>
              Cancelar
            </Button>

            <Button
              type="submit"
              isLoading={updateFaqIsPending}
              disabled={updateFaqIsPending}
              operationKlassToCheckPermission="Admin::Tmais::Faqs::Update"
              data-testid="update_tmais_faq"
            >
              Confirmar
            </Button>
          </div>
        }
      />

      <ErrorMessageWrapper className="flex-1" statusCode={faqError?.status} refetchFunction={refetchFaq}>
        {faqIsLoading ? (
          <Box>Carregando...</Box>
        ) : (
          <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-auto">
            <p className="text-lg font-bold">Editar FAQ</p>

            <TmaisFaqForm form={form} isSubmitting={updateFaqIsPending} />
          </Box>
        )}
      </ErrorMessageWrapper>

      {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
    </form>
  )
}

export default UpdateTmaisFaq
